import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowRight, Award } from "lucide-react";

/**
 * SteppingStonesPanel — collapsible right-side panel showing the top 3
 * lessons from the user's most recent stepping stone (interview reflection)
 * plus a button to log a new one.
 *
 * Empty state: gentle nudge — "After your next interview, capture lessons here."
 */
export default function SteppingStonesPanel({ recentStones }) {
  const navigate = useNavigate();
  const stones = recentStones || [];
  const latest = stones[0];

  return (
    <div className="space-y-3" data-testid="stones-panel">
      {!latest ? (
        <div className="text-[13px]" style={{ color: "#475569" }}>
          After your next mock or real interview, log what you learned. Resmo
          tracks your growth across interviews and surfaces patterns you might
          miss.
        </div>
      ) : (
        <>
          <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "#475569", fontWeight: 700 }}>
            Latest reflection · {latest.job_title || "Interview"}
            {latest.company_name && (
              <span style={{ color: "#94A3B8" }}> · {latest.company_name}</span>
            )}
          </div>
          {Array.isArray(latest.lessons_preview) && latest.lessons_preview.length > 0 ? (
            <ul className="space-y-1" data-testid="stones-panel-lessons">
              {latest.lessons_preview.map((lesson, idx) => (
                <li
                  key={idx}
                  className="px-3 py-2 rounded text-[12px] leading-snug"
                  style={{
                    background: "#FEF3C7",
                    border: "1px solid #FFC145",
                    color: "#451A03",
                  }}
                >
                  <strong>#{idx + 1}.</strong> {lesson}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[12px] px-3 py-2 rounded"
                 style={{ background: "#F8FAFC", border: "1px dashed #94A3B8", color: "#64748B" }}>
              No lessons captured yet on this stone. Open it to add some.
            </div>
          )}
          {/* Outcome chip */}
          {latest.outcome && (
            <div className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest rounded"
                 style={{
                   background:
                     latest.outcome === "offered" ? "#10B981" :
                     latest.outcome === "declined" ? "#DC2626" : "#94A3B8",
                   color: "white",
                 }}>
              {latest.outcome}
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => navigate(latest ? "/stepping-stones" : "/stepping-stones?new=1")}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[12px] font-semibold"
        style={{
          background: latest
            ? "white"
            : "linear-gradient(135deg, #10B981 0%, #047857 100%)",
          color: "#0F1B2D",
          border: "1px solid #0F1B2D",
          borderRadius: 8,
          fontWeight: 700,
        }}
        data-testid="stones-panel-cta"
      >
        {latest ? (
          <>
            <Heart size={12} className="rm-text-orange" /> View all stones
            <ArrowRight size={11} />
          </>
        ) : (
          <>
            <Award size={12} /> Log a stepping stone
            <ArrowRight size={11} />
          </>
        )}
      </button>
    </div>
  );
}
